import { useCallback, useEffect, useState } from "react";
import type { KeyboardEvent } from "react";
import { Avatar, Icon, IconButton, Input, SkeletonText, useToast } from "@astrum/ui";
import { stripMarkdown } from "@astrum/shared";
import { api, ApiError } from "../../lib/api.js";
import type { Channel, ChannelLabel, Message } from "../../lib/api.js";

/* The sidebar views that are not a channel list: cross-channel message lists
 * (Flagged / To-do / Done / Mentions / Threads) and the personal labels manager. */

function formatMessageWhen(iso: string): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: sameYear ? undefined : "numeric" });
}

/**
 * One hit in a cross-channel message list (Flagged/To-do/Done/Mentions/Threads).
 *
 * Clicking navigates to the message's channel AND jumps to the exact message
 * once that channel's history has loaded, via the same `?message=` deep-link
 * mechanism a notification uses (see `handleSelectChannel` and the
 * pagination-walking effect in useMessageJump). This used to just land in the
 * right conversation and leave the reader to scroll — the cross-channel jump
 * was skipped as "more plumbing than this list's job" — but that plumbing
 * already exists now for notification deep links, so this reuses it instead
 * of duplicating the reasoning for why it was left out.
 */
export function MessageHit({ message, onOpen }: { message: Message; onOpen: () => void }) {
  return (
    <button type="button" className="chat-list__hit" onClick={onOpen}>
      <Avatar name={message.authorName} size={22} />
      <span className="chat-list__hit-body">
        <span className="chat-list__hit-meta">
          <strong>{message.authorName}</strong>
          <span>{formatMessageWhen(message.createdAt)}</span>
          {message.myFlag && <span className={`chat-list__hit-badge is-${message.myFlag}`}>{message.myFlag}</span>}
        </span>
        <span className="chat-list__hit-text">{message.body ? stripMarkdown(message.body) : "Message deleted"}</span>
      </span>
    </button>
  );
}

/** A cross-channel message list, shared by Flagged/To-do/Done/Mentions/Threads. */
export function MessageFilterList({
  path,
  emptyLabel,
  onOpenChannel,
}: {
  path: string;
  emptyLabel: string;
  onOpenChannel: (channelId: string, messageId?: string) => void;
}) {
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ messages: Message[] }>(path)
      .then((res) => setMessages(res.messages))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Could not load this list.");
        setMessages(null);
      })
      .finally(() => setLoading(false));
  }, [path]);

  if (loading) {
    return (
      <div className="chat-list__loading">
        <SkeletonText lines={3} />
      </div>
    );
  }
  if (error) return <p className="chat-list__no-match">{error}</p>;
  if (!messages || messages.length === 0) return <p className="chat-list__no-match">{emptyLabel}</p>;

  return (
    <div className="chat-list__hits">
      {messages.map((message) => (
        <MessageHit key={message.id} message={message} onOpen={() => onOpenChannel(message.channelId, message.id)} />
      ))}
    </div>
  );
}

/**
 * Personal channel labels: create, rename, delete, and toggle a channel's
 * membership in each — the same idea as Lark's, kept private to whoever made
 * them so two people's "Urgent" never collide or need permission rules.
 */
export function LabelsPanel({
  channels,
  onOpenChannel,
}: {
  channels: Channel[];
  onOpenChannel: (channelId: string, messageId?: string) => void;
}) {
  const [labels, setLabels] = useState<ChannelLabel[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const { toast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ labels: ChannelLabel[] }>("/me/labels")
      .then((res) => setLabels(res.labels))
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "Could not load labels.", { tone: "danger" }))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(load, [load]);

  const createLabel = () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    api
      .post<{ label: ChannelLabel }>("/me/labels", { name })
      .then((res) => {
        setLabels((prev) => [...(prev ?? []), res.label]);
        setNewName("");
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "Could not create that label.", { tone: "danger" }))
      .finally(() => setCreating(false));
  };

  const renameLabel = (labelId: string) => {
    const name = editingName.trim();
    if (!name) return;
    api
      .patch<{ label: ChannelLabel }>(`/me/labels/${labelId}`, { name })
      .then(() => {
        setLabels((prev) => prev?.map((l) => (l.id === labelId ? { ...l, name } : l)) ?? null);
        setEditingId(null);
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "Could not rename that label.", { tone: "danger" }));
  };

  const deleteLabel = (labelId: string) => {
    api
      .delete(`/me/labels/${labelId}`)
      .then(() => setLabels((prev) => prev?.filter((l) => l.id !== labelId) ?? null))
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "Could not delete that label.", { tone: "danger" }));
  };

  const toggleChannel = (label: ChannelLabel, channelId: string) => {
    const applied = label.channelIds.includes(channelId);
    const request = applied
      ? api.delete(`/me/labels/${label.id}/channels/${channelId}`)
      : api.put(`/me/labels/${label.id}/channels/${channelId}`);
    request
      .then(() => {
        setLabels(
          (prev) =>
            prev?.map((l) =>
              l.id === label.id
                ? { ...l, channelIds: applied ? l.channelIds.filter((id) => id !== channelId) : [...l.channelIds, channelId] }
                : l,
            ) ?? null,
        );
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "Could not update that label.", { tone: "danger" }));
  };

  if (loading) {
    return (
      <div className="chat-list__loading">
        <SkeletonText lines={3} />
      </div>
    );
  }

  return (
    <div className="chat-labels">
      <div className="chat-labels__new">
        <Input
          placeholder="New label name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && createLabel()}
        />
        <IconButton icon={<Icon name="plus" size={15} />} label="Create label" size="sm" onClick={createLabel} disabled={creating || !newName.trim()} />
      </div>

      {!labels || labels.length === 0 ? (
        <p className="chat-list__no-match">No labels yet — make one to group your own channels.</p>
      ) : (
        <div className="chat-labels__list">
          {labels.map((label) => (
            <div key={label.id} className="chat-labels__item">
              <div className="chat-labels__row">
                <button
                  type="button"
                  className="chat-labels__toggle"
                  onClick={() => setExpandedId((id) => (id === label.id ? null : label.id))}
                >
                  <Icon name={expandedId === label.id ? "chevron-down" : "chevron-right"} size={13} />
                  <Icon name="pin" size={13} />
                  {editingId === label.id ? (
                    <input
                      autoFocus
                      className="chat-labels__rename-input"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") renameLabel(label.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={() => renameLabel(label.id)}
                    />
                  ) : (
                    <span>{label.name}</span>
                  )}
                  <span className="chat-labels__count">{label.channelIds.length}</span>
                </button>
                <IconButton
                  icon={<Icon name="edit" size={13} />}
                  label={`Rename ${label.name}`}
                  size="sm"
                  onClick={() => {
                    setEditingId(label.id);
                    setEditingName(label.name);
                  }}
                />
                <IconButton
                  icon={<Icon name="trash" size={13} />}
                  label={`Delete ${label.name}`}
                  size="sm"
                  onClick={() => deleteLabel(label.id)}
                />
              </div>

              {expandedId === label.id && (
                <div className="chat-labels__channels">
                  {label.channelIds.length === 0 && <p className="chat-labels__hint">Not applied to any channel yet.</p>}
                  {label.channelIds
                    .map((id) => channels.find((c) => c.id === id))
                    .filter((c): c is Channel => !!c)
                    .map((channel) => (
                      <button key={channel.id} type="button" className="chat-labels__channel" onClick={() => onOpenChannel(channel.id)}>
                        <span className="chat-list__hash" aria-hidden="true">
                          {channel.kind === "direct" ? "" : "#"}
                        </span>
                        {channel.name}
                      </button>
                    ))}
                  <details className="chat-labels__add-channel">
                    <summary>Add a channel…</summary>
                    <div className="chat-labels__add-list">
                      {channels
                        .filter((c) => !label.channelIds.includes(c.id))
                        .map((c) => (
                          <button key={c.id} type="button" onClick={() => toggleChannel(label, c.id)}>
                            {c.kind === "direct" ? "" : "#"} {c.name}
                          </button>
                        ))}
                    </div>
                  </details>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
